import { useEffect, useRef } from "react";

const useWebSocket = (url, onMessageCallback, onOpenCallback) => {
  const socketRef = useRef(null);
  const reconnectAttempts = useRef(0); // Track reconnection attempts

  const setupWebSocket = () => {
    if (socketRef.current) {
      socketRef.current.close();
    }

    console.log("🔄 Connecting WebSocket...");
    socketRef.current = new WebSocket(url);

    socketRef.current.onopen = () => {
      console.log("✅ WebSocket Connected");
      reconnectAttempts.current = 0; // Reset attempts after successful connection
      if (onOpenCallback) {
        onOpenCallback();
      }
    };

    socketRef.current.onmessage = (event) => {
      if (onMessageCallback) {
        onMessageCallback(event);
      }
    };

    socketRef.current.onerror = (error) => {
      console.error("⚠️ WebSocket Error:", error);
    };

    socketRef.current.onclose = () => {
      console.warn("❌ WebSocket Closed. Attempting to reconnect...");
      reconnectWebSocket();
    };
  };

  const reconnectWebSocket = () => {
    if (reconnectAttempts.current >= 10) {
      console.error(
        "🚨 Max reconnect attempts reached. Stopping reconnection."
      );
      return;
    }

    const delay = Math.min(3000 * 2 ** reconnectAttempts.current, 30000); // Exponential backoff (max 30 sec)
    reconnectAttempts.current += 1;

    setTimeout(() => {
      console.warn(
        `♻️ Reconnecting WebSocket... Attempt #${reconnectAttempts.current}`
      );
      setupWebSocket();
    }, delay);
  };

  useEffect(() => {
    setupWebSocket();

    return () => {
      if (socketRef.current) {
        socketRef.current.onclose = null; // Prevent unintended reconnections on unmount
        socketRef.current.close();
      }
    };
  }, [url]);

  const sendMessage = (message) => {
    if (socketRef.current && socketRef.current.readyState === WebSocket.OPEN) {
      socketRef.current.send(message);
      console.log("📤 Sent message:", message);
    } else {
      console.error(
        "❌ WebSocket is not open. Current state:",
        socketRef.current?.readyState
      );
    }
  };

  return { sendMessage };
};

export default useWebSocket;
